"use client";

import Image from "next/image";
import { usePathname } from "next/navigation";
import {
  CalendarCheck,
  LayoutDashboard,
  LogOut,
  Shield,
  ShieldCheck,
  Store,
  Timer,
} from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import React from "react";
import { Button } from "@hisani/ui";
import hisaniLogo from "../../../../brand/hisani_logo.png";
import { Link } from "../i18n/navigation";
import { localizedPath } from "../i18n/paths";
import { useAuth } from "./auth-client";
import { ProfileAvatar } from "./profile-avatar";

export function PlatformSidebar() {
  const auth = useAuth();
  const locale = useLocale();
  const pathname = usePathname();
  const t = useTranslations("Common");
  const tAuth = useTranslations("Auth");

  const normalizedPath = pathname.replace(/\/+$/u, "") || "/";
  const strippedPath = normalizedPath.startsWith(`/${locale}`)
    ? normalizedPath.slice(locale.length + 1) || "/"
    : normalizedPath;

  const isDashboard = strippedPath === "/dashboard";
  const logoHref = isDashboard ? "/" : "/dashboard";

  const isRouteActive = (href: string) => {
    if (href === "/dashboard") return strippedPath === "/dashboard";
    if (href === "/stopwatch") return strippedPath.startsWith("/stopwatch");
    if (href === "/planner") return strippedPath.startsWith("/planner");
    if (href === "/marketplace") return strippedPath.startsWith("/marketplace");
    if (href === "/stable") return strippedPath.startsWith("/stable");
    if (href === "/account") return strippedPath.startsWith("/account");
    if (href === "/admin") return strippedPath.startsWith("/admin");
    return false;
  };

  const navItems = [
    {
      href: "/dashboard",
      label: t("dashboard"),
      icon: LayoutDashboard,
      active: isRouteActive("/dashboard"),
    },
    {
      href: "/stopwatch",
      label: t("stopwatch"),
      icon: Timer,
      active: isRouteActive("/stopwatch"),
    },
    {
      href: "/planner",
      label: t("planner"),
      icon: CalendarCheck,
      active: isRouteActive("/planner"),
    },
    {
      href: "/marketplace",
      label: t("marketplace"),
      icon: Store,
      active: isRouteActive("/marketplace"),
    },
  ];

  return (
    <aside
      aria-label={t("primaryNavigation")}
      className="sticky top-0 hidden h-screen min-h-screen w-[var(--sidebar-width-collapsed)] shrink-0 flex-col justify-between overflow-y-auto border-e border-border-subtle bg-surface px-2 py-4 md:flex min-[1200px]:w-[var(--sidebar-width-expanded)] min-[1200px]:p-4"
    >
      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-center overflow-hidden pt-1 min-[1200px]:justify-start min-[1200px]:px-2">
          <Link
            href={logoHref}
            id="sidebar-logo-link"
            className="inline-flex min-h-11 items-center rounded-xs px-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
          >
            <Image
              src={hisaniLogo}
              alt={t("title")}
              width={130}
              height={64}
              priority
              className="h-10 w-auto max-w-none"
            />
          </Link>
        </div>

        <nav
          className="flex flex-col gap-1"
          aria-label={t("primaryNavigation")}
        >
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.href}
                href={item.href}
                aria-label={item.label}
                aria-current={item.active ? "page" : undefined}
                title={item.label}
                className={`flex min-h-11 items-center justify-center gap-3 rounded-md px-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-surface min-[1200px]:justify-start min-[1200px]:px-3 ${
                  item.active
                    ? "bg-brand-subtle text-brand border-s-2 border-brand"
                    : "text-secondary hover:bg-subtle hover:text-primary"
                }`}
              >
                <Icon className="size-4 shrink-0" aria-hidden="true" />
                <span className="hidden min-[1200px]:inline">{item.label}</span>
              </Link>
            );
          })}

          <Link
            href="/stable"
            aria-label={t("stable")}
            aria-current={isRouteActive("/stable") ? "page" : undefined}
            title={t("stable")}
            className={`flex min-h-11 items-center justify-center gap-3 rounded-md px-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-surface min-[1200px]:justify-start min-[1200px]:px-3 ${
              isRouteActive("/stable")
                ? "bg-brand-subtle text-brand border-s-2 border-brand"
                : "text-secondary hover:bg-subtle hover:text-primary"
            }`}
          >
            <Shield className="size-4 shrink-0" aria-hidden="true" />
            <span className="hidden min-[1200px]:inline">{t("stable")}</span>
          </Link>

          {auth.status === "authenticated" &&
          auth.account.platformRole === "ADMIN" ? (
            <Link
              href="/admin"
              aria-label={tAuth("adminDashboard")}
              aria-current={isRouteActive("/admin") ? "page" : undefined}
              title={tAuth("adminDashboard")}
              className={`flex min-h-11 items-center justify-center gap-3 rounded-md px-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-surface min-[1200px]:justify-start min-[1200px]:px-3 ${
                isRouteActive("/admin")
                  ? "bg-brand-subtle text-brand border-s-2 border-brand"
                  : "text-secondary hover:bg-subtle hover:text-primary"
              }`}
            >
              <ShieldCheck
                className="size-4 shrink-0 text-brand"
                aria-hidden="true"
              />
              <span className="hidden min-[1200px]:inline">
                {tAuth("adminDashboard")}
              </span>
            </Link>
          ) : null}
        </nav>
      </div>

      <div className="flex flex-col gap-3 border-t border-border-subtle pt-4">
        {auth.status === "authenticated" ? (
          <div className="flex flex-col gap-1">
            <Link
              href="/account"
              aria-label={tAuth("account")}
              aria-current={isRouteActive("/account") ? "page" : undefined}
              title={tAuth("account")}
              className={`flex min-h-11 items-center justify-center gap-2.5 rounded-md p-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-surface min-[1200px]:justify-start ${
                isRouteActive("/account")
                  ? "bg-brand-subtle text-brand"
                  : "hover:bg-subtle"
              }`}
            >
              <ProfileAvatar
                profilePictureUrl={auth.account.profilePictureUrl}
              />
              <div className="hidden min-w-0 flex-1 text-start min-[1200px]:block">
                <p className="text-body-small truncate font-bold text-primary">
                  {auth.account.displayName ??
                    auth.account.primaryRole ??
                    tAuth("account")}
                </p>
                <p className="text-body-small truncate text-secondary">
                  {auth.account.primaryRole ?? "Horseman"}
                </p>
              </div>
            </Link>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label={tAuth("logout")}
              title={tAuth("logout")}
              onClick={() => {
                void auth.logout(localizedPath(locale, "/"));
              }}
              className="w-full gap-2 px-2 text-danger hover:bg-danger-subtle min-[1200px]:justify-start"
            >
              <LogOut className="size-4" aria-hidden="true" />
              <span className="hidden min-[1200px]:inline">
                {tAuth("logout")}
              </span>
            </Button>
          </div>
        ) : (
          <Link
            href="/login"
            className="text-body-small inline-flex min-h-11 items-center justify-center rounded-md bg-brand px-3 font-bold text-on-brand shadow-elevation-1 transition-colors hover:bg-brand-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
          >
            {tAuth("loginSignup")}
          </Link>
        )}
      </div>
    </aside>
  );
}
